/**
 * Admin → Dealer desk → Accounts: every client's live account with money marked at current prices
 * (refreshed every few seconds), and one account's page with its open, pending and closed trades,
 * transactions and activity, with the dealer and money actions of AdminDeskDealing.
 */
import { useEffect, useState, type ReactNode } from 'react';
import { D, fmtDateTime, fmtUsd, fmtUsdSigned, rangeQuery } from '@platform/shared';
import { ClientCell, clientName } from '../components/ClientCell';
import { ClientNavBar, type ClientNav } from '../components/ClientNav';
import { periodRangeOf, TimeFrameSelect, type Period } from '../components/DateRange';
import { HelpTip } from '../components/HelpTip';
import { InstrumentLabel } from '../components/InstrumentIcon';
import { CopyId, cx, Empty, sideLabel, Spinner, TradeId, TransferTag } from '../components/ui';
import { api, ApiError } from '../lib/api';
import { useLoad } from '../stores/account';
import { useInstruments } from '../stores/market';
import { DeskActions, OrderActions, useDesk } from './AdminDeskDealing';
import { ClosePositionDialog, OrderDialog, PositionDialog, RestoreDialog, type Ord, type Pos } from './AdminDeskPositionDialogs';

const th = 'px-3 py-2 text-left text-[11px] font-medium text-muted whitespace-nowrap';
const td = 'px-3 py-2 whitespace-nowrap';
const num = 'num text-right';
const input = 'h-9 rounded-lg border border-line bg-bg px-3 text-[13px] text-fg outline-none focus:border-line-strong placeholder:text-muted';
const btn = 'h-8 rounded-md border border-line-strong px-3 text-xs font-semibold text-fg disabled:opacity-50';
const REFRESH_MS = 5000;

const pctOf = (x: string | null) => (x === null ? '—' : `${D.from(x).mul(100).toFixed(1)}%`);
const tone = (x: string | null) => (x === null ? '' : D.from(x).isNeg() ? 'text-down' : D.from(x).isPos() ? 'text-up' : '');
const Badge = ({ className, children }: { className: string; children: ReactNode }) => (
  <span className={cx('inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-semibold whitespace-nowrap', className)}>{children}</span>
);

/** Refetch on an interval while mounted (live money). */
function usePoll(reload: () => Promise<void>, ms = REFRESH_MS) {
  useEffect(() => {
    const t = window.setInterval(() => void reload(), ms);
    return () => window.clearInterval(t);
  }, [reload, ms]);
}

interface Flags {
  negativeBalance: boolean;
  marginWatch: boolean;
  pendingRequests: number;
  pendingWithdrawals: number;
}
function FlagBadges({ f }: { f: Flags }) {
  return (
    <span className="flex flex-wrap gap-1">
      {f.negativeBalance && <Badge className="bg-down/15 text-down">Negative balance</Badge>}
      {f.marginWatch && <Badge className="bg-warn/15 text-warn">Margin watch</Badge>}
      {f.pendingRequests > 0 && <Badge className="bg-warn/15 text-warn">{f.pendingRequests} request{f.pendingRequests > 1 ? 's' : ''} pending</Badge>}
      {f.pendingWithdrawals > 0 && <Badge className="bg-accent/10 text-accent">Withdrawal pending</Badge>}
    </span>
  );
}

// ---------- routing inside the tab: ?tab=accounts&account=<id> ----------

function useAccountParam(): [string | null, (id: string | null, replace?: boolean) => void] {
  const read = () => new URLSearchParams(window.location.search).get('account');
  const [id, setId] = useState<string | null>(read);
  useEffect(() => {
    const on = () => setId(read());
    window.addEventListener('popstate', on);
    return () => window.removeEventListener('popstate', on);
  }, []);
  /** `replace` swaps the history entry, so stepping between clients doesn't pile up behind Back. */
  const go = (next: string | null, replace = false) => {
    window.history[replace ? 'replaceState' : 'pushState']({}, '', `/admin?tab=accounts${next ? `&account=${next}` : ''}`);
    setId(next);
  };
  return [id, go];
}

export function DeskAccountsTab() {
  const [account, go] = useAccountParam();
  // The accounts on the grid page last shown, in its order, for previous / next on an account's page.
  const [list, setList] = useState<string[]>([]);
  const index = account ? list.indexOf(account) : -1;
  const nav: ClientNav | undefined = index >= 0 ? { index, total: list.length, go: (i) => list[i] && go(list[i], true) } : undefined;
  // Kept here so previous / next lands on the same tab; opening from the grid starts on Open.
  const [sub, setSub] = useState<Sub>('open');
  const open = (id: string) => {
    setSub('open');
    go(id);
  };
  return account ? <AccountPage key={account} id={account} onBack={() => go(null)} nav={nav} sub={sub} setSub={setSub} /> : <AccountsGrid onOpen={open} onRows={setList} />;
}

// ---------- grid ----------

interface GridRow {
  accountId: string;
  userId: string;
  name: string | null;
  email: string | null;
  externalClientId: string | null;
  status: string;
  tradingStatus: string;
  kycStatus: string;
  online: boolean;
  lastSeenAt: string | null;
  balance: string;
  credit: string;
  equity: string;
  openPnl: string;
  /** The sub-accounts' part of balance / equity / open P&L (which include it); null without any. */
  managed: { balance: string; equity: string; openPnl: string } | null;
  freeMargin: string;
  marginLevel: string | null;
  openPositions: number;
  flags: Flags;
}
interface Grid {
  total: number;
  online: number;
  totals: { balance: string; credit: string; equity: string; openPnl: string };
  rows: GridRow[];
}

const FILTERS = [
  { key: 'online', label: 'Online' },
  { key: 'open', label: 'Open positions' },
  { key: 'negative', label: 'Negative balance' },
  { key: 'pending', label: 'Pending requests' },
  { key: 'margin', label: 'Margin level < 150%' },
] as const;
type FilterKey = (typeof FILTERS)[number]['key'];
const COLS: { key: string; label: string; sort?: string; right?: boolean }[] = [
  { key: 'id', label: 'Account' },
  { key: 'name', label: 'Client', sort: 'name' },
  { key: 'online', label: 'Online' },
  { key: 'lastSeen', label: 'Last seen', sort: 'lastSeen' },
  { key: 'balance', label: 'Balance', sort: 'balance', right: true },
  { key: 'credit', label: 'Credit', sort: 'credit', right: true },
  { key: 'equity', label: 'Equity', sort: 'equity', right: true },
  { key: 'openPnl', label: 'Open P&L', sort: 'openPnl', right: true },
  { key: 'marginLevel', label: 'Margin level', sort: 'marginLevel', right: true },
];
const PAGE = 50;

function AccountsGrid({ onOpen, onRows }: { onOpen: (id: string) => void; onRows: (ids: string[]) => void }) {
  const [term, setTerm] = useState('');
  const [query, setQuery] = useState('');
  const [on, setOn] = useState<Record<FilterKey, boolean>>({ online: false, open: false, negative: false, pending: false, margin: false });
  const [status, setStatus] = useState('active');
  const [sort, setSort] = useState<{ by: string; dir: 'asc' | 'desc' }>({ by: 'name', dir: 'asc' });
  const [offset, setOffset] = useState(0);
  const qs = new URLSearchParams({ status, sort: sort.by, dir: sort.dir, limit: String(PAGE), offset: String(offset) });
  if (query) qs.set('q', query);
  for (const k of ['online', 'open', 'negative', 'pending'] as const) if (on[k]) qs.set(k, '1');
  if (on.margin) qs.set('marginBelow', '1.5');
  const { data, error, loading, reload } = useLoad<Grid>(`/api/admin/desk/accounts?${qs}`);
  usePoll(reload);
  const ids = data?.rows.map((r) => r.accountId).join(',');
  useEffect(() => {
    if (ids !== undefined) onRows(ids ? ids.split(',') : []);
  }, [ids, onRows]);
  const toggleSort = (by: string) => {
    setSort((s) => (s.by === by ? { by, dir: s.dir === 'asc' ? 'desc' : 'asc' } : { by, dir: by === 'name' ? 'asc' : 'desc' }));
    setOffset(0);
  };
  return (
    <div className="flex flex-col gap-3">
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <h2 className="m-0 text-lg font-semibold">Accounts</h2>
        {data && (
          <span className="text-xs text-muted">
            {data.total.toLocaleString()} accounts · {data.online} online {loading && <Spinner />}
          </span>
        )}
      </div>
      <form
        className="flex flex-wrap items-center gap-2"
        onSubmit={(e) => {
          e.preventDefault();
          setQuery(term.trim());
          setOffset(0);
        }}
      >
        <input className={cx(input, 'w-72')} type="search" placeholder="Search account, name or CRM ID" value={term} onChange={(e) => setTerm(e.target.value)} aria-label="Search accounts" />
        <select className={input} value={status} onChange={(e) => (setStatus(e.target.value), setOffset(0))} aria-label="State">
          <option value="active">Active</option>
          <option value="disabled">Disabled</option>
          <option value="all">All states</option>
        </select>
        {FILTERS.map((f) => (
          <button
            key={f.key}
            type="button"
            aria-pressed={on[f.key]}
            className={cx(btn, on[f.key] && 'border-accent bg-accent/10 text-accent')}
            onClick={() => (setOn({ ...on, [f.key]: !on[f.key] }), setOffset(0))}
          >
            {f.label}
          </button>
        ))}
      </form>
      {error && <p className="m-0 text-xs text-down">{error}</p>}
      <div className="overflow-x-auto rounded-xl border border-line bg-panel">
        <table className="w-full border-collapse text-xs" style={{ minWidth: 900 }}>
          <thead className="border-b border-line">
            <tr>
              {COLS.map((c) => (
                <th key={c.key} className={cx(th, c.right && 'text-right')} aria-sort={sort.by === c.sort ? (sort.dir === 'asc' ? 'ascending' : 'descending') : undefined}>
                  {c.sort ? (
                    <button type="button" className="font-medium text-muted hover:text-fg" onClick={() => toggleSort(c.sort!)}>
                      {c.label} {sort.by === c.sort ? (sort.dir === 'asc' ? '↑' : '↓') : ''}
                    </button>
                  ) : (
                    c.label
                  )}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {data?.rows.map((r) => (
              // The whole row opens the account; the name stays a button for the keyboard.
              <tr key={r.accountId} className="cursor-pointer border-b border-line hover:bg-raised/50" onClick={() => onOpen(r.accountId)}>
                <td className={cx(td, 'num text-muted')}>{r.accountId.slice(0, 8)}</td>
                <td className={td}>
                  <ClientCell name={r.name} email={r.email} crmId={r.externalClientId} onOpen={() => onOpen(r.accountId)}>
                    <span className="flex flex-wrap gap-1">
                      {r.status !== 'active' && <Badge className="bg-raised text-muted">Disabled</Badge>}
                      {r.tradingStatus !== 'enabled' && <Badge className="bg-warn/15 text-warn">{r.tradingStatus === 'close_only' ? 'Close only' : 'Trading blocked'}</Badge>}
                      <FlagBadges f={r.flags} />
                    </span>
                  </ClientCell>
                </td>
                <td className={td}>{r.online ? <Badge className="bg-up/15 text-up">● Online</Badge> : <span className="text-muted">Offline</span>}</td>
                <td className={cx(td, 'text-muted')}>{r.lastSeenAt ? fmtDateTime(r.lastSeenAt) : '—'}</td>
                <td className={cx(td, num, r.flags.negativeBalance && 'text-down')} title={r.managed ? splitText(r.balance, r.managed.balance) : undefined}>
                  {fmtUsd(r.balance)}
                  {r.managed && <span className="ml-1 text-managed" aria-label="includes managed money">●</span>}
                </td>
                <td className={cx(td, num)}>{fmtUsd(r.credit)}</td>
                <td className={cx(td, num)} title={r.managed ? splitText(r.equity, r.managed.equity) : undefined}>{fmtUsd(r.equity)}</td>
                <td className={cx(td, num, tone(r.openPnl))} title={r.managed ? splitText(r.openPnl, r.managed.openPnl) : undefined}>{fmtUsd(r.openPnl)}</td>
                <td className={cx(td, num, r.flags.marginWatch && 'text-down')}>{pctOf(r.marginLevel)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {data && !data.rows.length && <Empty>No accounts match.</Empty>}
      {data?.rows.some((r) => r.managed) && (
        <p className="m-0 text-[11px] text-muted">
          <span className="text-managed">●</span> Balance, Equity and Open P&amp;L include money on managed accounts, copies and Smart Portfolios, as the client sees it. Hover a figure for the split.
        </p>
      )}
      {data && (
        <div className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-line bg-panel px-3 py-2 text-xs">
          <span className="flex items-center gap-2">
            <button type="button" className={btn} disabled={offset === 0} onClick={() => setOffset(Math.max(0, offset - PAGE))}>
              ← Prev
            </button>
            <span className="text-muted">
              {data.total ? `${offset + 1}–${Math.min(offset + PAGE, data.total)} of ${data.total.toLocaleString()}` : '0'}
            </span>
            <button type="button" className={btn} disabled={offset + PAGE >= data.total} onClick={() => setOffset(offset + PAGE)}>
              Next →
            </button>
          </span>
          <span className="num flex flex-wrap gap-4 text-muted">
            <span>Balance <b className="text-fg">{fmtUsd(data.totals.balance)}</b></span>
            <span>Credit <b className="text-fg">{fmtUsd(data.totals.credit)}</b></span>
            <span>Equity <b className="text-fg">{fmtUsd(data.totals.equity)}</b></span>
            <span>Open P&amp;L <b className={tone(data.totals.openPnl)}>{fmtUsd(data.totals.openPnl)}</b></span>
          </span>
        </div>
      )}
    </div>
  );
}

// ---------- one account ----------

interface Detail {
  account: { id: string; currency: string };
  /** Set for a master account (Admin → Masters): the same screens, trading only. */
  master: { id: string; name: string; status: string; maxLeverage: number | null; maxOrder?: string | null; instruments: string[] } | null;
  client: {
    userId: string;
    name: string | null;
    email: string | null;
    externalClientId: string | null;
    status: string;
    tradingStatus: string;
    withdrawalStatus: string;
    /** Why staff set the restriction, if they said. */
    statusReason?: string | null;
    kycStatus: string;
    maxLeverage: number | null;
  };
  presence: { online: boolean; page: string | null; lastSeenAt: string | null; staff: { name: string; page: string | null; since?: string | null } | null };
  money: { balance: string; credit: string; equity: string; openPnl: string; marginUsed: string; freeMargin: string; marginLevel: string | null; exposure: string; openLots?: string };
  /** The client's running sub-accounts (managed by a master, copies, Smart Portfolios), each and summed; null without any. */
  managed?: { balance: string; equity: string; openPnl: string; openPositions: number; accounts: SubAccount[] } | null;
  /** Live + sub-accounts: the client's Total balance, Equity and Open P/L. */
  total?: { balance: string; equity: string; openPnl: string };
  levels: { warning: string; critical: string; stopOut: string };
  flags: Flags;
  /** open / closed: the live account's own; managed: the master-managed copies'. */
  counts: { open: number; pending: number; closed: number; transactions: number; managed?: { open: number; closed: number } };
}
interface SubAccount {
  accountId: string;
  source: { kind: 'master' | 'lead_trader' | 'portfolio'; id: string; name: string | null };
  /** The master's own account (a desk page of its own); null for a copy or Smart Portfolio. */
  masterAccountId: string | null;
  allocated: string;
  balance: string;
  equity: string;
  openPnl: string;
  /** Equity − allocated. */
  pnl: string;
  openPositions: number;
}
/** A label value, a line under it, and a hover text. */
type Tile = [string, string, string?, string?, string?];

const SOURCE_LABEL = { master: 'Managed', lead_trader: 'Copy', portfolio: 'Smart Portfolio' } as const;
const sourceName = (a: SubAccount) => `${SOURCE_LABEL[a.source.kind]}${a.source.name ? ` · ${a.source.name}` : ''}`;
/** 'Trading $3,771.44 + managed $2,000.00', from a total and its managed part. */
const splitText = (total: string, managed: string) => `Trading ${fmtUsd(D.from(total).sub(managed))} + managed ${fmtUsd(managed)}`;

/** Opens another account on this tab (the master a managed account follows). */
function openAccount(id: string) {
  window.history.pushState({}, '', `/admin?tab=accounts&account=${id}`);
  window.dispatchEvent(new PopStateEvent('popstate'));
}

/** One strip per sub-account, under the tiles: what runs it, what was allocated, and where it stands now. */
function SubAccountStrips({ accounts }: { accounts: SubAccount[] }) {
  return (
    <div className="flex flex-col gap-1.5">
      {accounts.map((a) => (
        <div key={a.accountId} className={cx('flex flex-wrap items-center gap-x-5 gap-y-1 rounded-lg px-3 py-2 text-xs', a.source.kind === 'portfolio' ? 'bg-raised' : 'bg-managed-bg')}>
          <span className={cx('font-semibold', a.source.kind === 'portfolio' ? 'text-fg' : 'text-managed')}>{sourceName(a)}</span>
          {(
            [
              ['Allocated', fmtUsd(a.allocated)],
              ['Balance', fmtUsd(a.balance)],
              ['Equity', fmtUsd(a.equity)],
              ['P&L since allocated', fmtUsdSigned(a.pnl), tone(a.pnl)],
              ['Open', `${a.openPositions} position${a.openPositions === 1 ? '' : 's'}`],
            ] as [string, string, string?][]
          ).map(([k, v, cls]) => (
            <span key={k} className="num flex gap-1.5">
              <span className="text-muted">{k}</span>
              <span className={cx('font-semibold', cls)}>{v}</span>
            </span>
          ))}
          {a.masterAccountId && (
            <button type="button" className="ml-auto text-xs font-semibold text-managed hover:underline" onClick={() => openAccount(a.masterAccountId!)}>
              Open master →
            </button>
          )}
        </div>
      ))}
    </div>
  );
}
type Sub = 'open' | 'pending' | 'closed' | 'transactions' | 'corrections' | 'activity';

function AccountPage({ id, onBack, nav, sub, setSub }: { id: string; onBack: () => void; nav?: ClientNav; sub: Sub; setSub: (s: Sub) => void }) {
  const { data, error, reload } = useLoad<Detail>(`/api/admin/desk/accounts/${id}`);
  usePoll(reload);
  // Bumped after an action so the open tab reloads with the money.
  const [rev, setRev] = useState(0);
  const refresh = () => {
    void reload();
    setRev((r) => r + 1);
  };
  if (error) return <p className="m-0 text-xs text-down">{error}</p>;
  if (!data) return <Spinner />;
  const m = data.money;
  // With sub-accounts, Balance / Equity / Open P&L are the client's totals (as their account bar shows
  // them), each with its split; the margin figures stay the trading account's and say so.
  const mg = data.managed ?? null;
  const t = data.total ?? { balance: m.balance, equity: m.equity, openPnl: m.openPnl };
  const split = (k: 'balance' | 'equity' | 'openPnl') => (mg ? `Trading ${fmtUsd(m[k])} + managed ${fmtUsd(mg[k])}` : undefined);
  const own = mg ? 'Trading account' : undefined;
  const balanceHelp = mg ? `Trading account ${fmtUsd(m.balance)} + ${mg.accounts.map((a) => `${sourceName(a)} ${fmtUsd(a.balance)}`).join(' + ')}.` : undefined;
  const tiles: Tile[] = [
    [mg ? 'Total balance' : 'Balance', fmtUsd(t.balance), data.flags.negativeBalance ? 'text-down' : '', split('balance'), balanceHelp],
    ['Credit', fmtUsd(m.credit)],
    ['Equity', fmtUsd(t.equity), '', split('equity')],
    ['Open P&L', fmtUsd(t.openPnl), tone(t.openPnl), split('openPnl')],
    ['Margin', fmtUsd(m.marginUsed), '', own],
    ['Free margin', fmtUsd(m.freeMargin), '', own, mg ? "For the client's own trades, on the trading account. Managed money is not part of it." : undefined],
    ['Margin level', pctOf(m.marginLevel), data.flags.marginWatch ? 'text-down' : '', own],
    ['Exposure', fmtUsd(m.exposure), '', own],
  ];
  return (
    <AccountTrading id={id} data={data} refresh={refresh} rev={rev} sub={sub} setSub={setSub} tiles={tiles} below={mg && <SubAccountStrips accounts={mg.accounts} />}>
      <div className="flex flex-wrap items-center justify-between gap-2">
        <button type="button" className="text-xs text-muted hover:text-fg" onClick={onBack}>
          ← Accounts
        </button>
        {nav && nav.total > 1 && <ClientNavBar nav={nav} />}
      </div>
      <div className="flex flex-col gap-1">
        <h2 className="m-0 text-lg font-semibold">
          {clientName({ name: data.client.name, email: data.client.email, crmId: data.client.externalClientId })}{' '}
          {data.client.externalClientId ? (
            <span className="font-mono text-sm font-normal text-muted">· CRM <CopyId value={data.client.externalClientId} label="CRM ID" /></span>
          ) : (
            <span className="rounded bg-raised px-1.5 py-px align-middle text-[11px] font-medium text-muted">No CRM ID</span>
          )}{' '}
          <span className="num text-sm font-normal text-muted">· account <CopyId value={data.account.id} label="account ID">{data.account.id.slice(0, 8)}</CopyId></span>{' '}
          {data.managed?.accounts
            .filter((a) => a.source.kind === 'master')
            .map((a) => (
              <Badge key={a.accountId} className="mr-1 bg-managed-bg align-middle text-managed">
                {sourceName(a)}
              </Badge>
            ))}
          {data.presence.online ? (
            <Badge className="bg-up/15 align-middle text-up">● Online{data.presence.page ? ` · ${data.presence.page}` : ''}</Badge>
          ) : (
            <Badge className="bg-raised align-middle text-muted">○ Offline{data.presence.lastSeenAt ? ` · last seen ${fmtDateTime(data.presence.lastSeenAt)}` : ''}</Badge>
          )}
        </h2>
        <div className="flex flex-wrap items-center gap-2 text-xs text-muted">
          {data.presence.staff && (
            <Badge className="bg-accent/10 text-accent">
              {data.presence.staff.name} is logged in as this client{data.presence.staff.since ? ` since ${fmtDateTime(data.presence.staff.since)}` : ''}
            </Badge>
          )}
          <span>{data.client.email}</span>
          <span>{data.account.currency}</span>
          <span>State {data.client.status}</span>
          <span>Trading {data.client.tradingStatus.replace('_', ' ')}</span>
          <span>Withdrawals {data.client.withdrawalStatus}</span>
          <span>KYC {data.client.kycStatus}</span>
          <FlagBadges f={data.flags} />
        </div>
      </div>
    </AccountTrading>
  );
}

/**
 * The trading part of an account page, shared with Admin → Masters: the money tiles, the Open position /
 * Money action / Close all bar, and the Open / Pending / Closed (…) tabs. `children` go above the tiles
 * (the client's header); `rowExtra` adds a button to each open position's row (the master's Followers).
 */
function AccountTrading({ id, data, refresh, rev, sub, setSub, tiles, subsOf, rowExtra, below, children }: { id: string; data: Detail; refresh: () => void; rev: number; sub: Sub; setSub: (s: Sub) => void; tiles: Tile[]; subsOf?: (all: [Sub, string][]) => [Sub, string][]; rowExtra?: (p: Pos) => ReactNode; below?: ReactNode; children?: ReactNode }) {
  const m = data.money;
  // Managed trades list with the client's own (All / Own / Managed), as on the client's Trade page.
  const mc = data.counts.managed ?? { open: 0, closed: 0 };
  const hasManaged = mc.open + mc.closed > 0;
  const all: [Sub, string][] = [
    ['open', `Open (${data.counts.open + mc.open})`],
    ['pending', `Pending (${data.counts.pending})`],
    ['closed', `Closed (${data.counts.closed + mc.closed})`],
    ['transactions', `Transactions (${data.counts.transactions})`],
    ['corrections', 'Corrections'],
    ['activity', 'Activity'],
  ];
  const subs = subsOf ? subsOf(all) : all;
  return (
    <DeskActions
      accountId={id}
      refresh={refresh}
      master={!!data.master}
      masterId={data.master?.id ?? null}
      leverageCap={data.master ? data.master.maxLeverage : data.client.maxLeverage}
      limits={{
        client: data.master ? null : { tradingStatus: data.client.tradingStatus, reason: data.client.statusReason ?? null, maxLeverage: data.client.maxLeverage, crmId: data.client.externalClientId },
        master: data.master ? { id: data.master.id, name: data.master.name, maxOrder: data.master.maxOrder ?? null, maxLeverage: data.master.maxLeverage, instruments: data.master.instruments } : null,
      }}
      summary={{ label: data.master?.name ?? data.client.externalClientId ?? data.account.id.slice(0, 8), balance: m.balance, credit: m.credit, equity: m.equity, openPnl: m.openPnl, freeMargin: m.freeMargin, marginLevel: m.marginLevel, marginUsed: m.marginUsed, stopOut: data.levels.stopOut, openLots: m.openLots }}
    >
      {(bar) => (
        <div className="flex flex-col gap-3">
      {children}
      <section className="grid grid-cols-2 gap-px overflow-hidden rounded-xl border border-line bg-line sm:grid-cols-4">
        {tiles.map(([label, value, cls, note, help]) => (
          <div key={label} className="flex flex-col gap-0.5 bg-panel px-3 py-2.5">
            <span className="flex items-center gap-1 text-[11px] text-muted">
              {label}
              {help && <HelpTip icon="info" help={{ title: label, text: help }} />}
            </span>
            <span className={cx('num text-sm font-semibold', cls)}>{value}</span>
            {note && <span className="num text-[11px] text-muted">{note}</span>}
          </div>
        ))}
      </section>
      {below}
      <p className="m-0 text-[11px] text-muted">
        Live. Alerts at {pctOf(data.levels.warning)} and {pctOf(data.levels.critical)}; stop-out at {pctOf(data.levels.stopOut)} (automatic).
      </p>
      {data.master?.status !== 'closed' && bar}
      <div role="tablist" className="flex flex-wrap gap-4 border-b border-line">
        {subs.map(([k, label]) => (
          <button
            key={k}
            type="button"
            role="tab"
            aria-selected={sub === k}
            className={cx('-mb-px border-b-2 px-1 py-2 text-xs font-semibold', sub === k ? 'border-accent text-fg' : 'border-transparent text-muted hover:text-fg')}
            onClick={() => setSub(k)}
          >
            {label}
          </button>
        ))}
      </div>
      {sub === 'open' && <OpenPositions key={rev} id={id} rowExtra={rowExtra} withManaged={hasManaged} />}
      {sub === 'pending' && <PendingOrders key={rev} id={id} />}
      {sub === 'closed' && <ClosedPositions key={rev} id={id} withManaged={hasManaged} />}
      {sub === 'transactions' && <Transactions key={rev} id={id} />}
      {sub === 'corrections' && <Corrections key={rev} id={id} />}
      {sub === 'activity' && <Activity key={rev} id={id} />}
        </div>
      )}
    </DeskActions>
  );
}

function Table({ head, right = [], children, minWidth = 900 }: { head: string[]; right?: string[]; children: ReactNode; minWidth?: number }) {
  return (
    <div className="overflow-x-auto rounded-xl border border-line bg-panel">
      <table className="w-full border-collapse text-xs" style={{ minWidth }}>
        <thead className="border-b border-line">
          <tr>
            {head.map((h) => (
              <th key={h} className={cx(th, right.includes(h) && 'text-right')}>
                {h}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>{children}</tbody>
      </table>
    </div>
  );
}
const Err = ({ e }: { e: string | null }) => (e ? <p className="m-0 text-xs text-down">{e}</p> : null);
/** "Edit…" on a position row: opens the edit pop-up (the row itself does too). */
const EditButton = ({ onClick }: { onClick: () => void }) => (
  <button type="button" className="h-7 rounded-md border border-line-strong px-2 text-[11px] font-semibold text-fg" onClick={(e) => (e.stopPropagation(), onClick())}>
    Edit…
  </button>
);


/** A position's size in lots, from its instrument's lot size (the API gives units). */
function useLots() {
  const instruments = useInstruments();
  const bySymbol = new Map(instruments.map((i) => [i.symbol, i.lotSize]));
  return (p: { symbol: string; qty: string }) => {
    const lot = bySymbol.get(p.symbol);
    return lot ? D.from(p.qty).div(lot) : null;
  };
}
const LotsCell = ({ lots, qty }: { lots: D | null; qty: string }) => (
  <td className={cx(td, num)} title={`${qty} units`}>
    {lots ? lots.toString() : qty}
  </td>
);
const marginOf = (p: Pos) => p.marginHeld ?? p.margin ?? null;

/** A position row on the account page: the client's own, or (scope=all) one on their managed account. */
type Row = Pos & { managedBy?: { masterId: string; name: string; accountId: string } };
type Source = 'all' | 'own' | 'managed';
const bySource = (rows: Row[], s: Source) => (s === 'all' ? rows : rows.filter((r) => (s === 'managed') === !!r.managedBy));

function SourceChips({ value, onChange }: { value: Source; onChange: (s: Source) => void }) {
  return (
    <div role="group" aria-label="Show" className="flex gap-1.5">
      {(
        [
          ['all', 'All'],
          ['own', 'Own'],
          ['managed', 'Managed'],
        ] as [Source, string][]
      ).map(([k, label]) => (
        <button
          key={k}
          type="button"
          aria-pressed={value === k}
          className={cx('h-7 rounded-full border px-3 text-xs font-semibold', value === k ? 'border-fg bg-fg text-bg' : 'border-line bg-panel text-muted hover:text-fg')}
          onClick={() => onChange(k)}
        >
          {label}
        </button>
      ))}
    </div>
  );
}
const ManagedTag = () => <span className="ml-1.5 rounded-full bg-managed-bg px-1.5 py-px text-[10.5px] font-semibold text-managed">Managed</span>;
/** In place of Edit… on a managed row: it is traded on the master, so it links there. */
const ManagedBy = ({ m }: { m: NonNullable<Row['managedBy']> }) => (
  <button
    type="button"
    title="Traded on the master: open the master to change it"
    className="rounded-full bg-managed-bg px-2 py-0.5 text-[11px] font-semibold whitespace-nowrap text-managed hover:underline"
    onClick={(e) => (e.stopPropagation(), openAccount(m.accountId))}
  >
    Managed · {m.name}
  </button>
);

function OpenPositions({ id, rowExtra, withManaged }: { id: string; rowExtra?: (p: Pos) => ReactNode; withManaged?: boolean }) {
  const [editing, setEditing] = useState<string | null>(null);
  /** Close… next to Edit…: the close pop-up, at the live price. */
  const [closing, setClosing] = useState<string | null>(null);
  const desk = useDesk();
  const [source, setSource] = useState<Source>('all');
  const { data, error, reload } = useLoad<{ data: Row[] }>(`/api/admin/desk/accounts/${id}/positions?status=open${withManaged ? '&scope=all' : ''}`);
  usePoll(reload);
  const lotsOf = useLots();
  const rows = bySource(data?.data ?? [], source);
  const nManaged = rows.filter((r) => r.managedBy).length;
  const sum = (k: 'swap' | 'unrealizedPnl') => rows.reduce((s, r) => s.add(r[k] ?? 0), D.ZERO).toFixed(2);
  const lotsTotal = rows.reduce((s, r) => s.add(lotsOf(r) ?? 0), D.ZERO);
  const marginTotal = rows.reduce((s, r) => s.add(marginOf(r) ?? 0), D.ZERO);
  const R = ['Lots', 'Margin', 'Lev.', 'Open', 'Current', 'SL', 'TP', 'Liq.', 'Swap', 'P&L'];
  return (
    <>
      {withManaged && <SourceChips value={source} onChange={setSource} />}
      <Err e={error} />
      <Table head={['Order ID', 'Position', 'Instrument', 'Side', 'Lots', 'Margin', 'Lev.', 'Opened', 'By', 'Open', 'Current', 'SL', 'TP', 'Liq.', 'Swap', 'P&L', '']} right={R} minWidth={1180}>
        {rows.map((p) => (
          <tr
            key={p.id}
            className={cx('border-b border-line', p.managedBy ? 'bg-managed-bg' : 'cursor-pointer hover:bg-raised/50')}
            onClick={p.managedBy ? undefined : () => setEditing(p.id)}
          >
            <td className={td}>
              <TradeId id={p.tradeId} />
              {p.source === 'transfer' && <TransferTag className="ml-1" t={p.transfer ?? { broker: null, ticket: null }} />}
            </td>
            <td className={cx(td, 'num text-muted')}>{p.id.slice(0, 8)}</td>
            <td className={cx(td, 'font-semibold')}>
              <InstrumentLabel symbol={p.symbol}>
                {p.symbol}
                {p.managedBy && <ManagedTag />}
              </InstrumentLabel>
            </td>
            <td className={cx(td, p.side === 'long' ? 'text-up' : 'text-down')}>{sideLabel(p.side)}</td>
            <LotsCell lots={lotsOf(p)} qty={p.qty} />
            <td className={cx(td, num)}>{marginOf(p) ? fmtUsd(marginOf(p)!) : '—'}</td>
            <td className={cx(td, num, 'text-muted')}>1:{p.leverage}</td>
            <td className={cx(td, 'text-muted')}>{fmtDateTime(p.openedAt)}</td>
            <td className={cx(td, 'text-muted')}>{p.managedBy ? `dealer · ${p.managedBy.name}` : (p.openedBy ?? '—')}</td>
            <td className={cx(td, num)}>{p.entryPrice}</td>
            <td className={cx(td, num)}>{p.currentPrice ?? '—'}</td>
            <td className={cx(td, num)}>{p.slPrice ?? '—'}</td>
            <td className={cx(td, num)}>{p.tpPrice ?? '—'}</td>
            <td className={cx(td, num, 'text-muted')}>{p.liqPrice ?? '—'}</td>
            <td className={cx(td, num, tone(p.swap))}>{fmtUsd(p.swap)}</td>
            <td className={cx(td, num, tone(p.unrealizedPnl ?? null))}>{p.unrealizedPnl === null || p.unrealizedPnl === undefined ? '—' : fmtUsd(p.unrealizedPnl)}</td>
            <td className={td}>
              <span className="flex items-center gap-1.5" onClick={(e) => e.stopPropagation()}>
                {rowExtra?.(p)}
                {p.managedBy ? <ManagedBy m={p.managedBy} /> : <EditButton onClick={() => setEditing(p.id)} />}
                {!p.managedBy && desk?.canTrade && (
                  <button type="button" className="h-7 rounded-md border border-down/40 px-2 text-[11px] font-semibold text-down" onClick={() => setClosing(p.id)}>
                    Close…
                  </button>
                )}
              </span>
            </td>
          </tr>
        ))}
      </Table>
      {editing && rows.some((r) => r.id === editing && !r.managedBy) && <PositionDialog pos={rows.find((r) => r.id === editing)!} closed={false} onClose={() => setEditing(null)} />}
      {closing && rows.some((r) => r.id === closing && !r.managedBy) && <ClosePositionDialog pos={rows.find((r) => r.id === closing)!} onClose={() => setClosing(null)} />}
      {data && !rows.length && <Empty>{source === 'managed' ? 'No open managed positions.' : 'No open positions.'}</Empty>}
      {rows.length > 0 && (
        <p className="num m-0 text-right text-xs text-muted">
          {rows.length} open{nManaged > 0 && nManaged < rows.length ? ` (${rows.length - nManaged} own, ${nManaged} managed)` : ''} · {lotsTotal.toString()} lots · margin {fmtUsd(marginTotal)} · swap <span className={tone(sum('swap'))}>{fmtUsd(sum('swap'))}</span> · P&amp;L <span className={tone(sum('unrealizedPnl'))}>{fmtUsd(sum('unrealizedPnl'))}</span>
        </p>
      )}
    </>
  );
}

/** Time frames for an account's history lists; days are UTC, as the rest of the desk. */
const HISTORY_PERIODS: { id: number; label: string }[] = [
  { id: 0, label: 'All time' },
  { id: 7, label: '7 days' },
  { id: 30, label: '30 days' },
  { id: 90, label: '90 days' },
];

function ClosedPositions({ id, withManaged }: { id: string; withManaged?: boolean }) {
  const [editing, setEditing] = useState<string | null>(null);
  const [restoring, setRestoring] = useState<string | null>(null);
  const desk = useDesk();
  // Restore: the client's own trades, not a stop-out (desk/trade-restore.ts says why for the rest).
  const mayRestore = (p: Row) => !!desk?.canRestore && !p.managedBy && p.closeReason !== 'liquidation' && p.source !== 'copy' && p.source !== 'portfolio';
  const [source, setSource] = useState<Source>('all');
  const [period, setPeriod] = useState<Period<number>>({ id: 0 });
  const range = periodRangeOf(period, (d) => d, 'utc');
  const [pages, setPages] = useState<Row[]>([]);
  const [next, setNext] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const load = async (before: string | null) => {
    setBusy(true);
    try {
      // Closed positions take UTC days, both inclusive.
      const days = range ? `&from=${range.from}&to=${range.to}` : '';
      const scope = withManaged ? '&scope=all' : '';
      const r = await api.get<{ data: Row[]; nextBefore: string | null }>(`/api/admin/desk/accounts/${id}/positions?status=closed&limit=100${scope}${days}${before ? `&before=${encodeURIComponent(before)}` : ''}`);
      setPages((p) => (before ? [...p, ...r.data] : r.data));
      setNext(r.nextBefore);
      setError(null);
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Could not load');
    } finally {
      setBusy(false);
    }
  };
  useEffect(() => {
    void load(null);
  }, [id, withManaged, range?.from, range?.to]); // eslint-disable-line react-hooks/exhaustive-deps
  const lotsOf = useLots();
  const shown = bySource(pages, source);
  const R = ['Lots', 'Margin', 'Lev.', 'Open', 'Close', 'Swap', 'Fees', 'P&L'];
  return (
    <>
      <div className="flex flex-wrap items-center justify-between gap-2">
        {withManaged ? <SourceChips value={source} onChange={setSource} /> : <span />}
        <TimeFrameSelect label="Closed positions period" zone="utc" presets={HISTORY_PERIODS} value={period} onChange={setPeriod} />
      </div>
      <Err e={error} />
      <Table head={['Order ID', 'Position', 'Instrument', 'Side', 'Lots', 'Margin', 'Lev.', 'Opened', 'Closed', 'Closed by', 'Open', 'Close', 'Swap', 'Fees', 'P&L', '']} right={R} minWidth={1180}>
        {shown.map((p) => (
          <tr
            key={p.id}
            className={cx('border-b border-line', p.managedBy ? 'bg-managed-bg' : 'cursor-pointer hover:bg-raised/50')}
            onClick={p.managedBy ? undefined : () => setEditing(p.id)}
          >
            <td className={td}>
              <TradeId id={p.tradeId} />
              {p.source === 'transfer' && <TransferTag className="ml-1" t={p.transfer ?? { broker: null, ticket: null }} />}
            </td>
            <td className={cx(td, 'num text-muted')}>{p.id.slice(0, 8)}</td>
            <td className={cx(td, 'font-semibold')}>
              <InstrumentLabel symbol={p.symbol}>
                {p.symbol}
                {p.managedBy && <ManagedTag />}
              </InstrumentLabel>
            </td>
            <td className={cx(td, p.side === 'long' ? 'text-up' : 'text-down')}>{sideLabel(p.side)}</td>
            <LotsCell lots={lotsOf(p)} qty={p.qty} />
            <td className={cx(td, num)}>{p.margin ? fmtUsd(p.margin) : '—'}</td>
            <td className={cx(td, num, 'text-muted')}>1:{p.leverage}</td>
            <td className={cx(td, 'text-muted')}>{fmtDateTime(p.openedAt)}</td>
            <td className={cx(td, 'text-muted')}>{p.closedAt ? fmtDateTime(p.closedAt) : '—'}</td>
            <td className={cx(td, 'text-muted')}>{p.closeReason ?? p.closedBy ?? '—'}</td>
            <td className={cx(td, num)}>{p.entryPrice}</td>
            <td className={cx(td, num)}>{p.closePrice ?? '—'}</td>
            <td className={cx(td, num, tone(p.swap))}>{fmtUsd(p.swap)}</td>
            <td className={cx(td, num)}>{fmtUsd(D.from(p.openFee).add(p.closeFee ?? 0))}</td>
            <td className={cx(td, num, tone(p.realizedPnl))}>{fmtUsd(p.realizedPnl)}</td>
            <td className={cx(td, 'whitespace-nowrap')}>
              {p.managedBy ? <ManagedBy m={p.managedBy} /> : <EditButton onClick={() => setEditing(p.id)} />}
              {mayRestore(p) && (
                <button type="button" className="ml-1 h-7 rounded-md border border-line-strong px-2 text-[11px] font-semibold text-fg" onClick={(e) => (e.stopPropagation(), setRestoring(p.id))}>
                  Restore…
                </button>
              )}
            </td>
          </tr>
        ))}
      </Table>
      {editing && pages.some((r) => r.id === editing && !r.managedBy) && (
        <PositionDialog
          pos={pages.find((r) => r.id === editing)!}
          closed
          onClose={() => setEditing(null)}
          onRestore={mayRestore(pages.find((r) => r.id === editing)!) ? () => (setEditing(null), setRestoring(editing)) : undefined}
        />
      )}
      {restoring && pages.some((r) => r.id === restoring) && <RestoreDialog pos={pages.find((r) => r.id === restoring)!} onClose={() => setRestoring(null)} />}
      {!busy && !shown.length && !error && <Empty>{range ? 'No closed positions in this period.' : 'No closed positions.'}</Empty>}
      {next && (
        <button type="button" className={cx(btn, 'self-center')} disabled={busy} onClick={() => void load(next)}>
          {busy ? <Spinner /> : 'Load more'}
        </button>
      )}
    </>
  );
}

/** Pending limit / stop orders; a row (or Edit…) opens Edit order: type, price, amount, leverage, TP / SL, or cancel. */
function PendingOrders({ id }: { id: string }) {
  const { data, error } = useLoad<Ord[]>(`/api/admin/desk/accounts/${id}/orders`);
  const [editing, setEditing] = useState<string | null>(null);
  return (
    <>
      <Err e={error} />
      <Table head={['Order ID', 'Ref', 'Instrument', 'Side', 'Type', 'Amount', 'Trigger', 'SL', 'TP', 'Placed', 'By', '']} right={['Amount', 'Trigger', 'SL', 'TP']}>
        {data?.map((o) => (
          <tr key={o.id} className="cursor-pointer border-b border-line hover:bg-raised/50" onClick={() => setEditing(o.id)}>
            <td className={td}>
              <TradeId id={o.tradeId} />
            </td>
            <td className={cx(td, 'num text-muted')}>{o.id.slice(0, 8)}</td>
            <td className={cx(td, 'font-semibold')}>
              <InstrumentLabel symbol={o.symbol} />
            </td>
            <td className={cx(td, o.side === 'long' ? 'text-up' : 'text-down')}>{sideLabel(o.side)}</td>
            <td className={td}>{o.type}</td>
            <td className={cx(td, num)}>{o.notional ? fmtUsd(o.notional) : `${o.lots} lots`}</td>
            <td className={cx(td, num)}>{o.limitPrice ?? o.stopPrice ?? '—'}</td>
            <td className={cx(td, num)}>{o.slPrice ?? '—'}</td>
            <td className={cx(td, num)}>{o.tpPrice ?? '—'}</td>
            <td className={cx(td, 'text-muted')}>{fmtDateTime(o.createdAt)}</td>
            <td className={cx(td, 'text-muted')}>{o.placedBy ?? '—'}</td>
            <td className={td}>
              <span className="flex items-center gap-1.5" onClick={(e) => e.stopPropagation()}>
                <EditButton onClick={() => setEditing(o.id)} />
                <OrderActions id={o.id} />
              </span>
            </td>
          </tr>
        ))}
      </Table>
      {editing && data?.some((o) => o.id === editing) && <OrderDialog order={data.find((o) => o.id === editing)!} onClose={() => setEditing(null)} />}
      {data && !data.length && <Empty>No pending orders.</Empty>}
    </>
  );
}

interface Entry {
  id: string;
  type: string;
  amount: string;
  balanceAfter: string;
  creditAmount: string | null;
  reason: string | null;
  createdAt: string;
}

function Transactions({ id }: { id: string }) {
  const [period, setPeriod] = useState<Period<number>>({ id: 0 });
  const range = periodRangeOf(period, (d) => d, 'utc');
  const rangeQs = range ? `&${rangeQuery(range, 'utc')}` : '';
  const [rows, setRows] = useState<Entry[]>([]);
  const [more, setMore] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const load = async (before?: string) => {
    setBusy(true);
    try {
      const r = await api.get<Entry[]>(`/api/admin/desk/accounts/${id}/ledger?limit=100${rangeQs}${before ? `&before=${before}` : ''}`);
      setRows((p) => (before ? [...p, ...r] : r));
      setMore(r.length === 100);
      setError(null);
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Could not load');
    } finally {
      setBusy(false);
    }
  };
  useEffect(() => {
    void load();
  }, [id, rangeQs]); // eslint-disable-line react-hooks/exhaustive-deps
  return (
    <>
      <div className="flex justify-end">
        <TimeFrameSelect label="Transactions period" zone="utc" times presets={HISTORY_PERIODS} value={period} onChange={setPeriod} />
      </div>
      <Err e={error} />
      <Table head={['Time', 'Type', 'ID', 'Funds in', 'Funds out', 'Credit', 'Balance after', 'Reason']} right={['Funds in', 'Funds out', 'Credit', 'Balance after']}>
        {rows.map((e) => {
          const amt = D.from(e.amount);
          return (
            <tr key={e.id} className="border-b border-line">
              <td className={cx(td, 'text-muted')}>{fmtDateTime(e.createdAt)}</td>
              <td className={td}>{e.type.replace(/_/g, ' ')}</td>
              <td className={cx(td, 'num text-muted')}>{e.id}</td>
              <td className={cx(td, num, 'text-up')}>{amt.isPos() ? fmtUsd(amt) : ''}</td>
              <td className={cx(td, num, 'text-down')}>{amt.isNeg() ? fmtUsd(amt.neg()) : ''}</td>
              <td className={cx(td, num)}>{e.creditAmount && !D.from(e.creditAmount).isZero() ? fmtUsd(e.creditAmount) : ''}</td>
              <td className={cx(td, num)}>{fmtUsd(e.balanceAfter)}</td>
              <td className={cx(td, 'max-w-80 truncate')} title={e.reason ?? ''}>
                {e.reason ?? ''}
              </td>
            </tr>
          );
        })}
      </Table>
      {!busy && !rows.length && !error && <Empty>{range ? 'No transactions in this period.' : 'No transactions.'}</Empty>}
      {more && (
        <button type="button" className={cx(btn, 'self-center')} disabled={busy} onClick={() => void load(rows[rows.length - 1]!.id)}>
          {busy ? <Spinner /> : 'Load more'}
        </button>
      )}
    </>
  );
}

interface AuditRow {
  id: string;
  at: string;
  actor: { name: string | null; role: string | null };
  channel: 'admin' | 'crm' | null;
  action: string;
  reason: string | null;
  before: Record<string, unknown> | null;
  after: Record<string, unknown> | null;
}

function Activity({ id }: { id: string }) {
  const { data, error } = useLoad<AuditRow[]>(`/api/admin/desk/accounts/${id}/activity`);
  const change = (b: AuditRow['before'], a: AuditRow['after']) =>
    a ? Object.keys(a).filter((k) => JSON.stringify(b?.[k]) !== JSON.stringify(a[k])).map((k) => `${k}: ${b?.[k] ?? '—'} → ${a[k]}`).join(', ') : '';
  return (
    <>
      <Err e={error} />
      <Table head={['Time', 'Staff', 'Screen', 'Action', 'Reason', 'Change']}>
        {data?.map((r) => (
          <tr key={r.id} className="border-b border-line">
            <td className={cx(td, 'text-muted')}>{fmtDateTime(r.at)}</td>
            <td className={td}>{r.actor.name ?? '—'}</td>
            <td className={cx(td, 'text-muted')}>{r.channel === 'crm' ? 'CRM' : r.channel === 'admin' ? 'Platform' : '—'}</td>
            <td className={cx(td, 'num')}>{r.action}</td>
            <td className={td}>{r.reason ?? ''}</td>
            <td className={cx(td, 'num')}>{change(r.before, r.after)}</td>
          </tr>
        ))}
      </Table>
      {data && !data.length && <Empty>No staff activity on this client yet.</Empty>}
    </>
  );
}

interface Correction {
  id: string;
  positionId: string;
  symbol: string;
  kind: string;
  amount: string;
  before: Record<string, unknown>;
  after: Record<string, unknown>;
  reasonCode: string;
  evidence: string;
  clientNote: string;
  requestedBy: string;
  postedBy: string;
  postedAt: string;
}
interface PendingCorrection {
  id: string;
  number: string;
  kind: string;
  preview: { symbol?: string; kind?: string; amount?: string };
  reason: string;
  requestedBy: string;
  requestedAt: string;
}
const KIND_LABEL: Record<string, string> = { price: 'Price', cancel: 'Cancel trade', commission: 'Commission', rollover: 'Rollover', transfer: 'Transferred trade', edit: 'Trade edit', restore: 'Trade restored' };
const vals = (o: Record<string, unknown>) => Object.entries(o).map(([k, v]) => `${k}: ${String(v)}`).join(', ');
const changeOf = (b: Record<string, unknown>, a: Record<string, unknown>) => (vals(b) ? `${vals(b)} → ${vals(a)}` : vals(a));

function Corrections({ id }: { id: string }) {
  const { data, error } = useLoad<{ posted: Correction[]; pending: PendingCorrection[] }>(`/api/admin/desk/accounts/${id}/corrections`);
  return (
    <>
      <Err e={error} />
      {!!data?.pending.length && (
        <div className="flex flex-col gap-1 rounded-xl border border-warn/40 bg-warn/5 p-3 text-xs">
          <b>Waiting for approval</b>
          {data.pending.map((r) => (
            <span key={r.id}>
              APR-{r.number} · {KIND_LABEL[r.preview.kind ?? ''] ?? r.kind} on {r.preview.symbol && <InstrumentLabel symbol={r.preview.symbol} size={14} className="gap-1" />} · <span className="num">{r.preview.amount ? fmtUsd(r.preview.amount) : ''}</span> · by {r.requestedBy}
            </span>
          ))}
        </div>
      )}
      <Table head={['Posted', 'Position', 'Type', 'Amount', 'Change', 'Reason', 'Evidence', 'Client note', 'Asked / posted by']} right={['Amount']} minWidth={1100}>
        {data?.posted.map((c) => (
          <tr key={c.id} className="border-b border-line align-top">
            <td className={cx(td, 'text-muted')}>{fmtDateTime(c.postedAt)}</td>
            <td className={cx(td, 'num')}>
              <InstrumentLabel symbol={c.symbol}>
                {c.symbol} <span className="text-muted">{c.positionId.slice(0, 8)}</span>
              </InstrumentLabel>
            </td>
            <td className={td}>{KIND_LABEL[c.kind] ?? c.kind}</td>
            <td className={cx(td, num, tone(c.amount))}>{fmtUsd(c.amount)}</td>
            <td className={cx(td, 'num')}>{changeOf(c.before, c.after)}</td>
            <td className={td}>{c.reasonCode.replace(/_/g, ' ')}</td>
            <td className={cx(td, 'max-w-56 truncate')} title={c.evidence}>
              {c.evidence}
            </td>
            <td className={cx(td, 'max-w-56 truncate')} title={c.clientNote}>
              {c.clientNote}
            </td>
            <td className={cx(td, 'text-muted')}>
              {c.requestedBy} / {c.postedBy}
            </td>
          </tr>
        ))}
      </Table>
      {data && !data.posted.length && <Empty>No corrections on this account.</Empty>}
    </>
  );
}

/**
 * Admin → Masters → Trade: the master account on the Dealer Desk's own screens (tiles, Open position,
 * Close all, Open / Pending / Closed tabs, the edit and restore pop-ups, Corrections), trading only. The
 * desk API accepts a master's account and routes its trades through copy/masters.ts (caps, copy signals,
 * CRM webhook).
 */
export function MasterTrading({ accountId, rowExtra, onChanged }: { accountId: string; rowExtra?: (p: Pos) => ReactNode; onChanged?: () => void }) {
  const { data, error, reload } = useLoad<Detail>(`/api/admin/desk/accounts/${accountId}`);
  usePoll(reload);
  const [rev, setRev] = useState(0);
  const [sub, setSub] = useState<Sub>('open');
  const refresh = () => {
    void reload();
    setRev((r) => r + 1);
    onChanged?.();
  };
  if (error) return <p className="m-0 text-xs text-down">{error}</p>;
  if (!data) return <Spinner />;
  const m = data.money;
  const tiles: [string, string, string?][] = [
    ['Balance', fmtUsd(m.balance), data.flags.negativeBalance ? 'text-down' : ''],
    ['Equity', fmtUsd(m.equity)],
    ['Open P&L', fmtUsd(m.openPnl), tone(m.openPnl)],
    ['Open lots', m.openLots ? `${m.openLots} lots` : '—'],
    ['Margin', fmtUsd(m.marginUsed)],
    ['Free margin', fmtUsd(m.freeMargin)],
    ['Margin level', pctOf(m.marginLevel), data.flags.marginWatch ? 'text-down' : ''],
    ['Exposure', fmtUsd(m.exposure)],
  ];
  return <AccountTrading id={accountId} data={data} refresh={refresh} rev={rev} sub={sub} setSub={setSub} tiles={tiles} subsOf={(all) => all.filter(([k]) => k === 'open' || k === 'pending' || k === 'closed' || k === 'transactions' || k === 'corrections')} rowExtra={rowExtra} />;
}
